import { useTranslation } from "react-i18next";

import type { StageComment, StageSnapshot } from "@msc/shared";

import { formatIsoDate } from "../../lib/date.js";
import { priorityLabel } from "../../lib/customerView/priorityLabel.js";
import { sanitizeForDisplay } from "../../lib/customerView/richHtml.js";

/**
 * Die Ticket-Folie: Schlüssel und Titel groß, darunter die Eckdaten als
 * Badges; links die Beschreibung des Vorgangs und — falls erzeugt — der
 * „Aktuelle Stand" (die geprüfte KI-Zusammenfassung aus dem Snapshot), rechts
 * der Verlauf.
 *
 * Der Verlauf zeigt gerendertes Jira-HTML. Es hat auf dem Server bereits
 * `sanitize-html` passiert; `sanitizeForDisplay` (DOMPurify) ist der zweite
 * Riegel unmittelbar vor dem Einsetzen — die Bühne ist das Fenster, in das
 * der Kunde schaut, und sie setzt nichts ungeprüft ein.
 *
 * Interne Kommentare stehen hier nur, wenn der Lead sie im Cockpit **einzeln**
 * freigegeben hat (`internal: true` im Snapshot); sie tragen dann sichtbar ihr
 * Kennzeichen, damit im Raum niemand über die Herkunft rätselt.
 */
function StageComments({ comments }: { comments: StageComment[] }) {
  const { t } = useTranslation();
  if (comments.length === 0) return null;
  // Neueste zuerst — der Snapshot liefert sie in Jira-Reihenfolge (älteste zuerst).
  const ordered = [...comments].reverse();
  return (
    <div className="stage-comments">
      <div className="stage-label">{t("stage.comments")}</div>
      <ul className="stage-list">
        {ordered.map((c) => (
          <li key={c.id}>
            <div className="stage-comment-meta">
              <span className="stage-comment-author">{c.authorName}</span>
              <span>{formatIsoDate(c.createdIso)}</span>
              {c.internal && (
                <span className="stage-comment-internal">{t("stage.internal")}</span>
              )}
            </div>
            <div
              className="stage-comment-body"
              dangerouslySetInnerHTML={{ __html: sanitizeForDisplay(c.bodyHtml) }}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

export function TicketSlide({ snapshot }: { snapshot: StageSnapshot }) {
  const { t } = useTranslation();
  const current = snapshot.current;
  if (!current) return null;
  const { ticket } = current;
  const priority = priorityLabel(ticket.priority?.name);
  // Die Bühne bricht nie (Spec D5): Ein Snapshot eines älteren Cockpit-Tabs
  // (laufender Rollout) kennt das Feld noch nicht — dann eben keine Beschreibung.
  const descriptionHtml = current.descriptionHtml ?? "";
  // Nur aus `comments` — was dort nicht steht, wird auch über den Fokus nicht
  // gezeigt (zweiter Riegel hinter `buildStageSnapshot`).
  const focused = current.focusedCommentId
    ? current.comments.find((c) => c.id === current.focusedCommentId) ?? null
    : null;

  // Ohne Verlauf bekommt der Stand die volle Breite statt einer leeren Spalte.
  const bodyClass =
    current.comments.length === 0
      ? "stage-ticket-body stage-ticket-body-single"
      : "stage-ticket-body";

  return (
    <div className="stage-slide stage-slide-ticket">
      <div className="stage-ticket-head">
        <div className="stage-key">{ticket.key}</div>
        <h1 className="stage-title">{ticket.title}</h1>

        <div className="stage-badges">
          <span className="stage-badge stage-badge-status">{ticket.status}</span>
          {priority !== "" && (
            <span className="stage-badge stage-badge-accent">
              {t("stage.priority")}: {priority}
            </span>
          )}
          {current.dueDate && (
            <span className="stage-badge">
              {t("stage.dueDate")}: {formatIsoDate(current.dueDate)}
            </span>
          )}
          {ticket.assigneeName && (
            <span className="stage-badge">
              {t("stage.assignee")}: {ticket.assigneeName}
            </span>
          )}
        </div>
      </div>

      {/*
        Stand links, Verlauf rechts über die volle Höhe (stage.css,
        `.stage-ticket-body`): Der Verlauf ist im Termin das Wichtigste und lag
        untereinander gestapelt in der unteren Hälfte eingequetscht — auf dem
        16:9-Beamer ist Breite da, Höhe nicht.
      */}
      <div className={bodyClass}>
        <div className="stage-ticket-side">
          <div className="stage-card stage-card-description">
            <div className="stage-label">{t("stage.description")}</div>
            {descriptionHtml.trim() === "" ? (
              <p className="stage-card-text stage-card-muted">{t("stage.noDescription")}</p>
            ) : (
              <div
                className="stage-card-html"
                dangerouslySetInnerHTML={{ __html: sanitizeForDisplay(descriptionHtml) }}
              />
            )}
          </div>
          {current.summaryText !== "" && (
            <div className="stage-card stage-card-current">
              <div className="stage-label">{t("stage.currentState")}</div>
              <p className="stage-card-text">{current.summaryText}</p>
            </div>
          )}
        </div>

        <StageComments comments={current.comments} />
      </div>

      {focused && (
        // Wie das gezeigte Bild: groß über der Folie, weil die Verlaufsspalte
        // auf der Bühne nicht scrollt — ein Hervorheben darin fände den
        // Kommentar nicht, wenn er weiter unten steht.
        <div className="stage-comment-focus">
          <div className="stage-comment-focus-card">
            {/* Das Overlay deckt Schlüssel und Titel der Folie zu — ohne
                diese Zeile wüsste im Raum niemand, zu welchem Ticket der
                Kommentar gehört. */}
            <div className="stage-comment-focus-ticket">
              {ticket.key} · {ticket.title}
            </div>
            <div className="stage-comment-meta">
              <span className="stage-comment-author">{focused.authorName}</span>
              <span>{formatIsoDate(focused.createdIso)}</span>
              {focused.internal && <span className="stage-comment-internal">{t("stage.internal")}</span>}
            </div>
            <div
              className="stage-comment-body"
              dangerouslySetInnerHTML={{ __html: sanitizeForDisplay(focused.bodyHtml) }}
            />
          </div>
        </div>
      )}

      {current.shownAttachment && (
        <div className="stage-attachment">
          <img
            src={`/api/v1/customer-view/attachments/${current.shownAttachment.id}`}
            alt={current.shownAttachment.filename}
          />
          <div className="stage-attachment-name">{current.shownAttachment.filename}</div>
        </div>
      )}
    </div>
  );
}
